import { AnyAction, createAsyncThunk, createSlice, isAnyOf, PayloadAction, Reducer, ThunkDispatch, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  isVideo,
  isWeb,
  fileExists, checkWritePermissions, isImage,
} from '../../app/utils';
import { getMaxUploadSize } from '../settings/SettingsSlice';
import { viewFile } from '../ui/UISlice';
import {
  loadMine as loadMineAPI,
  loadProjectMembers as loadProjectMembersAPI,
  loadRepo as loadRepoAPI,
  loadLegacyRepo as loadLegacyRepoAPI,
  getOpenLink as getOpenLinkAPI,
  getLegacyOpenLink as getLegacyOpenLinkAPI,
  downloadFile as downloadFileAPI,
  downloadLegacyFile as downloadLegacyFileAPI,
  uploadChunks as uploadChunksAPI,
  getFileThumbnail as getFileThumbnailAPI,
  getLegacyFileThumbnail as getLegacyFileThumbnailAPI,
  getFile as getFileAPI,
  getLegacyFile as getLegacyFileAPI,
  deleteFile as deleteFileAPI,
  generateFileDownloadLink as generateFileDownloadLinkAPI,
  generateLegacyFileDownloadLink as generateLegacyFileDownloadLinkAPI,
  getFilePreview as getFilePreviewAPI,
  getLegacyFilePreview as getLegacyFilePreviewAPI,
  renameFile as renameFileAPI,
  Project,
  ProjectMember,
  ProjectEntity,
  UploadOptions,
  ParsingType,
  ThumbnailOptions,
  RepoId,
  getStatus as getStatusAPI,
  ServerStatus,
  getProjectToken,
  Progress,
  getFileDetails as getFileDetailsAPI,
} from './ProjectsAPI';
import {Directory} from "@capacitor/filesystem";
import { createTransform, persistReducer } from 'redux-persist';
import { Capacitor } from '@capacitor/core';
import { CapacitorStorage } from '../../app/persist';
import projectMappings from '../../resources/project_mappings.json';
import { logout } from '../account/AccountSlice';
import { getStoredUser } from '../auth/cognitoAuth';

type BaseUploadStatus = {
  uploadedBytes: number;
  totalBytes: number;
  error?: Error;
  state: 'uploading' | 'canceled';
}

type BaseDownloadStatus = {
  downloadedBytes: number;
  totalBytes: number;
  error?: Error;
  state: 'downloading' | 'canceled';
}

type UploadingStatus = BaseUploadStatus & {
  state: 'uploading';
  controller?: AbortController;
}

export type DownloadingStatus = BaseDownloadStatus & {
  state: 'downloading';
}

type UploadCanceledStatus = BaseUploadStatus & {
  state: 'canceled';
  data?: {
    repo: RepoId;
    folder: string;
    file: File;
    options?: UploadOptions;
  };
}

export type DownloadCanceledStatus = BaseDownloadStatus & {
  state: 'canceled';
  data?: {
    repo: RepoId;
    folder: string;
    entity: ProjectEntity;
    options?: UploadOptions;
  };
}

type CanceledStatus = UploadCanceledStatus | DownloadCanceledStatus;

export function isCanceledStatus(status: UploadStatus | DownloadStatus): status is CanceledStatus{
  return status.state === 'canceled';
}

export function isUploadingStatus(status: UploadStatus): status is UploadingStatus{
  return status.state === 'uploading';
}

export type UploadStatus = UploadingStatus | UploadCanceledStatus;

export type DownloadStatus = DownloadingStatus | DownloadCanceledStatus;

export function isDownloadingStatus(status: DownloadStatus): status is DownloadingStatus{
  return status.state === 'downloading';
}

export type ProjectSession = {
  AccessKeyId: string;
  Expiration: string;
  SecretAccessKey: string;
  SessionToken: string;
}

export interface ProjectsState {
  downloadedFiles: Record<string, any>;
  loadedThumbnails: Record<string, { thumbnail: string, timestamp: number, transient?: boolean; size?: number}>;
  loadedFolder: Record<string, ProjectEntity[]>;
  loadedDetails: Record<string, ProjectEntity>,
  loadedProjectMembers: Record<string, ProjectMember[]>;
  personalLib: RepoId | null;
  mine: Project[] | null;
  currentUploads: Record<string, UploadStatus>;
  currentDownloads: Record<string, DownloadStatus>;
  entityToRename: FileIdentity | null;
  status: 'idle' | 'loading' | 'failed';
  error: Error | null;
  downloadLinks: Record<string, {url: string, timestamp: number}>
  previewLinks: Record<string, {url: string, timestamp: number, thumbnail?: string}>
  serverStatus: ServerStatus,
  sessions: Record<string, ProjectSession>
}

const initialState: ProjectsState = {
  downloadedFiles: {},
  loadedThumbnails: {},
  loadedFolder: {},
  loadedDetails: {},
  loadedProjectMembers: {},
  personalLib: null,
  mine: null,
  currentUploads: {},
  currentDownloads: {},
  entityToRename: null,
  status: 'idle',
  error: null,
  downloadLinks: {},
  previewLinks: {},
  serverStatus: 'unknown',
  sessions: {},
};

async function loadMineProjects(force: boolean, state: RootState, sessionToken: string): Promise<Project[]>{
  if(!force){
    const alreadyLoaded = getMine(state);
    if(alreadyLoaded) return alreadyLoaded;
  }
  const response = await loadMineAPI(sessionToken);
  return response;
}

type AuthorizedActionArgs = {
  token: string;
}

export const loadMine = createAsyncThunk<Project[], AuthorizedActionArgs>(
  'projects/load_mine',
  async ({ token }, { rejectWithValue, getState }) => {
    try{
      const state = await getState() as RootState;
      return await loadMineProjects(false, state, token);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const initEnvironment = createAsyncThunk<void, AuthorizedActionArgs>(
  'projects/init_environment',
  async ({ token }, { rejectWithValue, dispatch }) => {
    try{
      await dispatch(loadMine({ token }));
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const loadMineForcely = createAsyncThunk<Project[], AuthorizedActionArgs>(
  'projects/load_mine_forcely',
  async ({ token }, { rejectWithValue, getState }) => {
    try{
      const state = await getState() as RootState;
      return await loadMineProjects(true, state, token);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

type loadProjectMembersResult = {
  projectId: string;
  members: ProjectMember[];
}

export const loadProjectMembers = createAsyncThunk<loadProjectMembersResult, { projectId: string } & AuthorizedActionArgs>(
  'projects/load_project_member',
  async ({ projectId, token }, { rejectWithValue, dispatch }) => {
    try{
      const { project } = await retrieveProject(dispatch, projectId, token);
      if(!project) throw new Error("Progetto non trovato.");
      const response = await loadProjectMembersAPI(
        project.id,
        project.folders,
        token
      );
      return {
        projectId,
        members: response
      };
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const ensureProjectToken = createAsyncThunk<
  { storageToken: ProjectSession; projectId: string },
  { projectId: string } &
  AuthorizedActionArgs
>(
  'projects/ensure_project_token',
  async ({ projectId, token }, { rejectWithValue, getState }) => {
    try{
      const state = await getState() as RootState;
      let storageToken = getValidStorageToken(projectId)(state);
      if (!storageToken) {
        storageToken = await getProjectToken(token, projectId);
      }
      return {
        projectId,
        storageToken
      };
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export type RepoFolderScanOptions = {
  repoId: string;
  folder: string;
}

type loadRepoFolderResult = {
  repoId: string;
  folder: string;
  entities: ProjectEntity[]
}

function getValidStorageToken(repoId: string) {
  const getter = getStorageToken(repoId);
  return (state: RootState): ProjectSession | undefined => {
    const session = getter(state)
    if(session) {
      const expiration = new Date(session.Expiration)
      if (expiration.getTime() > Date.now()) {
        return session;
      }
    }
    return undefined;
  }
}

class AuthorizationError extends Error {
  public projectId: string;
  constructor(projectId: string) {
    super("Non hai effettuato l'accesso.");
    this.name = 'AuthorizationError';
    this.projectId = projectId;
  }
}

export const loadRepoFolder = createAsyncThunk<
loadRepoFolderResult, RepoFolderScanOptions
>(
  'projects/load_repo_folder',
  async ({ repoId, folder }, { rejectWithValue, getState }) => {
    try{
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state)
      if (!storageToken) throw new AuthorizationError(repoId);
      const response = await loadRepoAPI(repoId, folder, storageToken);
      if (state.account.legacyToken && ["/media", "/feed"].includes(folder)) {
        const legacyFolder = folder.slice(1) as "media" | "feed";
        try {
          const project = projectMappings.find(p => p.new === repoId);
          if (project) {
            const legacyResponse = await loadLegacyRepoAPI(project[legacyFolder], "/", state.account.legacyToken);
            response.push(...legacyResponse);
          }
        } catch (error) {
          console.warn("Failed to load legacy repo:", error);
        }
      }
      return {
        entities: response,
        folder,
        repoId
      };
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const loadPersonalLibrary = createAsyncThunk(
  'projects/load_personal_library',
  async ({ token }: { token: string }, { rejectWithValue, getState, dispatch }) => {
    try{
      const state = await getState() as RootState;
      await dispatch(loadMine({ token })).unwrap()
      return getPersonalRepoId(state);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export type FileIdentity = {
  repoId: RepoId;
  folder: string;
  file: ProjectEntity;
}

export const openFile = createAsyncThunk(
  'projects/open_file',
  async (
    { repoId, folder, file }: FileIdentity, { rejectWithValue, getState, dispatch }
  ) => {
    const state = await getState() as RootState;
    try{
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      const response = await getOpenLinkAPI(
        repoId, folder, file.name, storageToken
      );
      dispatch(viewFile(response));
    }catch(error){
      if (error instanceof Error && error.name === "NotFound" && state.account.legacyToken) {
        try {
          const legacyRepo = projectMappings.find(p => p.new === repoId);
          if (legacyRepo && ["media", "feed"].includes(folder.slice(1))) {
            const response = await getLegacyOpenLinkAPI(
              legacyRepo[folder.slice(1) as "media" | "feed"],
              "/",
              file.name,
              state.account.legacyToken
            );
            dispatch(viewFile(response));
            return;
          }
        } catch (error) {
          console.warn("Failed to load legacy open link:", error);
        }
      }
      return rejectWithValue(error);
    }
  }
);

export const downloadFile = createAsyncThunk(
  'projects/download_file',
  async (
    { repoId, folder, file }: FileIdentity, { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);

      const filename = file.name;

      if (isWeb()) { // Download da browser
        try {
          return await downloadFileAPI(repoId, folder, filename, storageToken);
        } catch(error) {
          if (error instanceof Error && error.name === "NotFound" && state.account.legacyToken) {
            try {
              const legacyRepo = projectMappings.find(p => p.new === repoId);
              if (legacyRepo && ["media", "feed"].includes(folder.slice(1))) {
                return await downloadLegacyFileAPI(
                  legacyRepo[folder.slice(1) as "media" | "feed"],
                  "/",
                  file.name,
                  state.account.legacyToken
                );
              }
            } catch (error) {
              console.warn("Failed to load legacy open link:", error);
            }
          }
          throw error;
        }
      } else { // Download nativo

        const location = `${repoId}/${folder}/${filename}`;
        const state = await getState() as RootState
        const previousDownload = getDownloadStatus(location)(state);
        if (previousDownload?.state === 'downloading')
          throw new Error('Stai già scaricando questo file');

        dispatch(setDownloadStatus({ repoId, folder, filename, status: startingStatus() }));

        await checkWritePermissions();

        const directory = Directory.ExternalStorage;
        const path = `Download/${filename}`;
        if (
          !(await fileExists(path)) ||
          window.confirm('File già scaricato! Vuoi sostituirlo?')
        ) {
          // use middleware
          return {
            repoId,
            folder,
            filename,
            storageToken,
            location,
            path,
            directory,
            file
          }
        }
      }
    } catch(error){
      return rejectWithValue(error);
    }
  }
);

async function retrieveProject(
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  repoId: string,
  token: string,
) {
  const projects = await dispatch(loadMine({ token })).then(unwrapResult);
  const project = projects.find(project => project.id === repoId);
  return { project };
}

function startingStatus(): DownloadStatus {
  return {
    downloadedBytes: 0,
    totalBytes: 1,
    state: 'downloading'
  }
}

type GetFileArguments = FileIdentity & {
  parse: ParsingType;
}
type GetFileResponse = {
  downloaded: any;
  entity: ProjectEntity;
}

export const getFile = createAsyncThunk(
  'projects/get_file',
  async (
    { repoId, folder, file, parse }: GetFileArguments,
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = await getState() as RootState;
    try{
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      const response = await downloadFileAPI(
        repoId, folder, file.name, storageToken,
        { getInApp: true, parseResponse: parse }
      );
      const toReturn: GetFileResponse = {
        downloaded: response,
        entity: file
      }
      return toReturn;
    }catch(error){
      if (error instanceof Error && error.name === "NotFound" && state.account.legacyToken) {
        try {
          const legacyRepo = projectMappings.find(p => p.new === repoId);
          if (legacyRepo && ["media", "feed"].includes(folder.slice(1))) {
            return await downloadLegacyFileAPI(
              legacyRepo[folder.slice(1) as "media" | "feed"],
              "/",
              file.name,
              state.account.legacyToken
            );
          }
        } catch (error) {
          console.warn("Failed to load legacy open link:", error);
        }
      }
      return rejectWithValue(error);
    }
  }
);

type GetFileDetailsArguments = Omit<FileIdentity, 'file'> & {
  fileId: string;
}

export const loadFileDownloadLink = createAsyncThunk(
  'projects/get_file_download_link',
  async (
    { repoId, folder, file }: FileIdentity,
    { rejectWithValue, getState, dispatch }
  ) => {
    const state = await getState() as RootState;
    try{
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      const downloadLink = await generateFileDownloadLinkAPI(
        repoId, folder, file.name, storageToken
      );
      return {file, downloadLink};
    }catch(error){
      if (error instanceof Error && error.name === "NotFound" && state.account.legacyToken) {
        try {
          const legacyRepo = projectMappings.find(p => p.new === repoId);
          if (legacyRepo && ["media", "feed"].includes(folder.slice(1))) {
            const legacyDownloadLink = await generateLegacyFileDownloadLinkAPI(
              legacyRepo[folder.slice(1) as "media" | "feed"],
              "/",
              file.name,
              state.account.legacyToken
            );
            return {file, downloadLink: legacyDownloadLink};
          }
        } catch (error) {
          console.warn("Failed to load legacy open link:", error);
        }
      }
      return rejectWithValue(error);
    }
  }
);

export const loadFilePreviewLink = createAsyncThunk(
  'projects/get_file_preview_link',
  async (
    { repoId, folder, file, forceCreation }: FileIdentity & { forceCreation?: boolean },
    { rejectWithValue, getState }
  ) => {
    const state = await getState() as RootState;
    try{
      const storedUser = await getStoredUser();
      if(!storedUser) throw new AuthorizationError(repoId);
      const preview = await getFilePreviewAPI(
        repoId, folder.substring(1), file, storedUser, forceCreation
      );
      return {file, downloadLink: preview.url, thumbnail: preview.thumbnail};
    }catch(error){
      if (error instanceof Error && error.message === "Impossibile continuare. Assicurati di avere i permessi per proseguire e che la tua connessione sia attiva." && state.account.legacyToken) {
        try {
          const legacyRepo = projectMappings.find(p => p.new === repoId);
          if (legacyRepo && ["media", "feed"].includes(folder.slice(1))) {
            const preview =  await getLegacyFilePreviewAPI(
              legacyRepo.old,
              folder.slice(1),
              file,
              state.account.legacyToken,
              forceCreation
            );
            return {file, downloadLink: preview.url, thumbnail: preview.thumbnail};
          }
        } catch (error) {
          console.warn("Failed to load legacy open link:", error);
        }
      }
      return rejectWithValue(error);
    }
  }
);

export const loadFileDetails = createAsyncThunk(
  'projects/get_file_details',
  async (
    { repoId, folder, fileId }: GetFileDetailsArguments,
    { rejectWithValue, getState }
  ) => {
    try{
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state)
      if(!storageToken) throw new AuthorizationError(repoId);
      return await getFileDetailsAPI(repoId, folder, fileId, storageToken);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export type FileIdentityRequest = {
  repoId: string;
  folder: string;
  file: File;
  options?: UploadOptions
}

type FileUploadStatusUpdate = {
  repoId: RepoId;
  folder: string;
  filename: string;
  status: UploadStatus;
}

type FileDownloadStatusUpdate = {
  repoId: RepoId;
  folder: string;
  filename: string;
  status: DownloadStatus
}

export const uploadFile = createAsyncThunk(
  'projects/upload_file',
  async (
    { repoId, folder, file, options }: FileIdentityRequest,
    { rejectWithValue, getState, dispatch }
  ) => {
    const filename = options?.toReplace?.name || file.name;
    let sentBytes = 0;
    try{
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      const location = `${repoId}/${folder}/${filename}`;
      const previousUpload = getUploadStatus(location)(state);
      if(previousUpload?.state === 'uploading')
        throw new Error('Stai già caricando questo file');
      try{
        const maxSize = getMaxUploadSize(state);
        dispatch(setUploadStatus({
          repoId,
          folder,
          filename,
          status: {
            uploadedBytes: 0,
            totalBytes: file.size,
            state: 'uploading'
          }
        }));
        const controller = AbortController ? new AbortController() : undefined;
        await uploadChunksAPI(
          repoId,
          folder,
          filename,
          file,
          maxSize,
          (progress: Progress) => {
            sentBytes = progress.loaded || 0;
            dispatch(setUploadStatus({
              repoId,
              folder,
              filename,
              status: {
                uploadedBytes: progress.loaded || 0,
                totalBytes: file.size,
                state: 'uploading',
                controller
              }
            }));
          },
          storageToken,
          controller,
        )
        dispatch(removeUploadStatusEntry(location));
        return {
          ok: true,
          filename: file.name,
        };
      }catch(error){
        let updatedState = await getState() as RootState;
        const status = getUploadStatus(location)(updatedState);
        dispatch(setUploadStatus({
          repoId,
          folder,
          filename,
          status: {
            uploadedBytes: sentBytes,
            totalBytes: file.size,
            state: 'canceled',
            error: status?.error || (error as Error),
            data: {
              repo: repoId,
              folder,
              file,
              options
            }
          }
        }));
        throw error;
      }
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

type FileThumbnailRequest = FileIdentity & {
  thumbnailOptions?: ThumbnailOptions
}

type ThumbnailRetrievalResponse = {
  file: ProjectEntity;
  thumbnail: string | null;
  transient: boolean;
  size: number;
}

export const getFileThumbnail = createAsyncThunk(
  'projects/get_file_thumbnail',
  async (
    { repoId, folder, file, thumbnailOptions }: FileThumbnailRequest,
    { rejectWithValue, getState, dispatch }
  ) => {
    try{
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      let thumbnail: {
        url: string | null;
        cachable: boolean;
      } = await getFileThumbnailAPI(
        repoId, folder, file, storageToken, thumbnailOptions
      );
      let legacy = false;
      if (!thumbnail.url && state.account.legacyToken) {
        try {
          const legacyRepo = projectMappings.find(p => p.new === repoId);
          if (legacyRepo && ["media", "feed"].includes(folder.slice(1))) {
            thumbnail = await getLegacyFileThumbnailAPI(
              legacyRepo[folder.slice(1) as "media" | "feed"],
              "/",
              file,
              state.account.legacyToken,
              thumbnailOptions
            );
            legacy = true;
          }
        } catch (error) {
          console.warn("Failed to load legacy file thumbnail:", error);
        }
      }
      let thumbnailUrl = thumbnail.url
      if (thumbnailUrl && !thumbnail.cachable) {
        const blob = legacy && state.account.legacyToken
          ? (await getLegacyFileAPI(thumbnailUrl, state.account.legacyToken)) as Blob
          : (await getFileAPI(thumbnailUrl)) as Blob
        thumbnailUrl = URL.createObjectURL(blob)
      }
      const response: ThumbnailRetrievalResponse = {
        file,
        thumbnail: thumbnailUrl,
        transient: !thumbnail.cachable,
        size: thumbnailOptions?.size ?? 256
      };
      return response;
    }catch(error){
      return rejectWithValue(error);
    }
  }
);


export const testServerStatus = createAsyncThunk(
  'projects/test_server_status',
  async (_, { rejectWithValue }) => {
    try{
      const response = await getStatusAPI();
      return response;
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const deleteFile = createAsyncThunk(
  'projects/delete_file',
  async (
    { repoId, folder, file }: FileIdentity, { rejectWithValue, getState, dispatch }
  ) => {
    try{
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      await deleteFileAPI(repoId, folder, file.name, storageToken);
    }catch(error){
      return rejectWithValue(error);
    }
  }
);

export const renameFile = createAsyncThunk(
  'projects/rename_file',
  async (
    { entity, newName }: { entity: FileIdentity, newName: string },
    { rejectWithValue, getState, dispatch }
  ) => {
    let { repoId, folder, file } = entity;
    try{
      const state = await getState() as RootState;
      const storageToken = getValidStorageToken(repoId)(state);
      if (!storageToken) throw new AuthorizationError(repoId);
      await renameFileAPI(repoId, folder, file.name, newName, storageToken);
      if(isVideo(newName) || isImage(newName)){
        const filenameSlices = file.name.split('.');
        const filenameSlicesNew = newName.split('.');
        const oldThumbnailName1 = `${filenameSlices.slice(0, -1).join('.')}.tmb`;
        const newThumbName1 = `${filenameSlicesNew.slice(0, -1).join('.')}.tmb`;
        const oldThumbnailName2 = `${filenameSlices.slice(0, -1).join('.')}_${filenameSlices[filenameSlices.length - 1]}.tmb`;
        const newThumbName2 = `${filenameSlicesNew.slice(0, -1).join('.')}_${filenameSlicesNew[filenameSlicesNew.length - 1]}.tmb`;
        const oldThumbnailName3 = `${filenameSlices.slice(0, -1).join('.')}_${filenameSlices[filenameSlices.length - 1]}.256.tmb`;
        const newThumbName3 = `${filenameSlicesNew.slice(0, -1).join('.')}_${filenameSlicesNew[filenameSlicesNew.length - 1]}.256.tmb`;
        const oldThumbnailName4 = `${filenameSlices.slice(0, -1).join('.')}_${filenameSlices[filenameSlices.length - 1]}.1280.tmb`;
        const newThumbName4 = `${filenameSlicesNew.slice(0, -1).join('.')}_${filenameSlicesNew[filenameSlicesNew.length - 1]}.1280.tmb`;
        for(
          const [oldName, newName] of
          [
            [oldThumbnailName1, newThumbName1],
            [oldThumbnailName2, newThumbName2],
            [oldThumbnailName3, newThumbName3],
            [oldThumbnailName4, newThumbName4]
          ]
        ){
          try{
            await renameFileAPI(
              repoId, folder, oldName, newName, storageToken
            );
          }catch{}
        }
      }
      return { entity, newName }
    }catch(error){
      return rejectWithValue(error);
    }
  }
);


export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setEntityToRename: (state, action: PayloadAction<FileIdentity>) => {
      state.entityToRename = action.payload;
    },
    removeEntityToRename: (state) => {
      state.entityToRename = null;
    },
    setUploadStatus: (state, action: PayloadAction<FileUploadStatusUpdate>) => {
      const update = action.payload;
      const location = `${update.repoId}/${update.folder}/${update.filename}`;
      state.currentUploads[location] = update.status;
    },
    cancelUpload: (state, action: PayloadAction<string>) => {
      const location = action.payload;
      let controller = null;
      const status = state.currentUploads[location];
      if(isUploadingStatus(status))
        controller = status.controller;
      state.currentUploads = {
        ...state.currentUploads,
        [location]: {
          ...status,
          state: 'canceled',
          error: new Error("Annullato")
        }
      };
      controller?.abort();
    },
    removeUploadStatusEntry: (state, action: PayloadAction<string>) => {
      const location = action.payload;
      delete state.currentUploads[location];
    },
    setDownloadStatus: (state, action: PayloadAction<FileDownloadStatusUpdate | { location: string; status: DownloadStatus}>) => {
      let location: string
      if ("location" in action.payload) location = action.payload.location
      else {
        const download = action.payload;
        location = `${download.repoId}/${download.folder}/${download.filename}`;
      }
      state.currentDownloads[location] = action.payload.status;
    },
    cancelDownload: (state, action: PayloadAction<string>) => {
      const location = action.payload;
      const status = state.currentDownloads[location];
      state.currentDownloads = {
        ...state.currentDownloads,
        [location]: {
          ...status,
          state: 'canceled',
          error: new Error("Annullato")
        }
      };
    },
    removeDownloadStatusEntry: (state, action: PayloadAction<string>) => {
      const location = action.payload;
      delete state.currentDownloads[location];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFileDetails.fulfilled, (state, action: PayloadAction<ProjectEntity>) => {
        state.status = 'idle';
        const entity = action.payload;
        state.loadedDetails[entity.id] = entity;
      })
      .addCase(
        loadProjectMembers.fulfilled,
        (state, action: PayloadAction<loadProjectMembersResult>) => {
          state.status = 'idle';
          state.loadedProjectMembers[action.payload.projectId] =
            action.payload.members;
        }
      )
      .addCase(testServerStatus.fulfilled, (state, action: PayloadAction<ServerStatus>) => {
        state.serverStatus = action.payload;
      })
      .addCase(testServerStatus.pending, (state) => {
        state.serverStatus = "unknown";
      })
      .addCase(
        loadRepoFolder.fulfilled,
        (state, action: PayloadAction<loadRepoFolderResult>) => {
          state.status = 'idle';
          const payload = action.payload;
          const virtualPath = `${payload.repoId}/${payload.folder}`;
          state.loadedFolder[virtualPath] = payload.entities;
        }
      )
      .addCase(
        getFileThumbnail.fulfilled,
        (state, action: PayloadAction<ThumbnailRetrievalResponse>) => {
          state.status = 'idle';
          const { file, thumbnail, transient, size } = action.payload;
          if(
            thumbnail &&
            (
              !state.loadedThumbnails[file.id] ||
              (state.loadedThumbnails[file.id].size ?? 0) < size
            )
          ){
            state.loadedThumbnails = {
              ...state.loadedThumbnails,
              [file.id]: {
                thumbnail,
                timestamp: Date.now(),
                transient,
                size
              }
            };
          }
        }
      )
      .addCase(
        getFile.fulfilled,
        (state, action: PayloadAction<GetFileResponse>) => {
          state.status = 'idle';
          const { entity, downloaded } = action.payload;
          state.downloadedFiles[entity.id] = downloaded;
        }
      )
      .addCase(
        loadPersonalLibrary.fulfilled,
        (state, action: PayloadAction<RepoId | undefined>) => {
          state.status = 'idle';
          state.personalLib = action.payload || null;
        }
      )
      .addCase(
        loadFileDownloadLink.fulfilled,
        (state, action: PayloadAction<{file: ProjectEntity, downloadLink: string}>) => {
          state.status = 'idle';
          const { downloadLink, file } = action.payload
          state.downloadLinks = {
            ...state.downloadLinks,
            [file.id]: {
              url: downloadLink,
              timestamp: Date.now()
            }
          }
        }
      )
      .addCase(
        loadFilePreviewLink.fulfilled,
        (state, action: PayloadAction<{
          file: ProjectEntity;
          downloadLink: string;
          thumbnail?: string;
        }>) => {
          state.status = 'idle';
          const { downloadLink, file, thumbnail } = action.payload
          state.previewLinks = {
            ...state.previewLinks,
            [file.id]: {
              url: downloadLink,
              thumbnail,
              timestamp: Date.now()
            }
          }
        }
      )
      .addCase(
        ensureProjectToken.fulfilled,
        (state, action: PayloadAction<{
          storageToken: ProjectSession;
          projectId: string;
        }>) => {
          state.status = 'idle';
          const { projectId, storageToken } = action.payload
          state.sessions = {
            ...state.sessions,
            [projectId]: storageToken,
          }
        }
      )
      .addCase(
        renameFile.fulfilled,
        (state, action: PayloadAction<{ entity: FileIdentity, newName: string }>) => {
          state.status = 'idle';
          const entity = action.payload.entity;
          const changed = entity.file;
          const virtualPath = `${entity.repoId}/${entity.folder}`;
          let nameIndex = 0;
          let newName = action.payload.newName.toLowerCase();
          while(
            state.loadedFolder[virtualPath]
              // eslint-disable-next-line no-loop-func
              .find(file => file.name === newName)
          ){
            nameIndex++;
            const lowered = action.payload.newName.toLowerCase();
            const onlyName = lowered.split('.').slice(0, -1).join('.');
            const extension = lowered.split('.').slice(-1).join('');
            newName = `${onlyName} (${nameIndex}).${extension}`;
          }
          state.loadedFolder[virtualPath] = state.loadedFolder[virtualPath].map(
            file => {
              if(file.name === changed.name){
                return {
                  ...file,
                  name: newName
                };
              }else
                return file;
            }
          )
        }
      )
      .addMatcher(
        isAnyOf(loadMine.fulfilled, loadMineForcely.fulfilled),
          (state, action: PayloadAction<Project[]>) => {
          state.status = 'idle';
          state.mine = action.payload;
        }
      )
      .addMatcher(
        isAnyOf(
          loadMine.pending, loadProjectMembers.pending, loadRepoFolder.pending,
          openFile.pending, downloadFile.pending, uploadFile.pending,
          getFileThumbnail.pending, getFile.pending, deleteFile.pending,
          loadFileDetails.pending, loadPersonalLibrary.pending, loadFileDownloadLink.pending,
          renameFile.pending, loadMineForcely.pending, loadFilePreviewLink.pending,
          testServerStatus.pending,
        ),
        (state) => {
          state.status = 'loading';
          state.error = null;
        }
      )
      .addMatcher(
        isAnyOf(
          loadMine.rejected, loadProjectMembers.rejected, loadRepoFolder.rejected,
          openFile.rejected, downloadFile.rejected, uploadFile.rejected,
          getFileThumbnail.rejected, getFile.rejected, deleteFile.rejected,
          loadFileDetails.rejected, loadPersonalLibrary.rejected, loadFilePreviewLink.rejected,
          loadFileDownloadLink.rejected, renameFile.rejected, loadMineForcely.rejected,
          testServerStatus.rejected,
        ),
        (state, action) => {
          state.status = 'failed';
          const error = action.payload;
          state.error = (error instanceof Error) ? error : new Error(error as string);
          if (error instanceof AuthorizationError) {
            state.sessions = { ...state.sessions, }
            delete state.sessions[error.projectId]
          }
        }
      )
      .addMatcher(
        isAnyOf(initEnvironment.fulfilled, logout.fulfilled),
        (state) => {
          state.downloadedFiles = {};
          state.loadedThumbnails = {};
          state.loadedFolder = {};
          state.loadedDetails = {};
          state.loadedProjectMembers = {};
          state.personalLib = null;
          state.currentUploads = {};
          state.downloadLinks = {};
          state.previewLinks = {};
          state.mine = null;
        }
      );
  },
});

export const {
  cancelUpload, setUploadStatus, cancelDownload, setDownloadStatus, removeUploadStatusEntry, removeDownloadStatusEntry, setEntityToRename,
  removeEntityToRename
} = projectsSlice.actions;

export const isLoading = (state: RootState) => state.projects.status === 'loading';
export const getError = (state: RootState) => state.projects.error;
export const getMine = (state: RootState) => state.projects.mine;
export const getLoadedProjectMembers = (projectId: string) =>
  (state: RootState): ProjectMember[] | undefined =>
    state.projects.loadedProjectMembers[projectId];
export const getProject =
  (id: string) =>
    (state: RootState) => state.projects.mine?.find(project => project.id === id);
export const getLoadedFolder = (projectId: string, folder: string) =>
  (state: RootState) => state.projects.loadedFolder[`${projectId}/${folder}`];
export const getLoadedFileThumbnail =
  (entity: ProjectEntity) =>
    (state: RootState): string | undefined => state.projects.loadedThumbnails[entity.id]?.thumbnail;
export const getDownloaded =
  (entity: ProjectEntity) =>
    (state: RootState): any => state.projects.downloadedFiles[entity.id];
export const getFileDetails =
  (entityId: string) =>
    (state: RootState) => state.projects.loadedDetails[entityId];
export const getPersonalLibrary =
  (state: RootState) => state.projects.personalLib;
export const getCurrentUploadsMap =
    (state: RootState) => state.projects.currentUploads;
export const getCurrentDownloadsMap =
    (state: RootState) => state.projects.currentDownloads;
export const getUploadStatus = (location: string) =>
  (state: RootState): UploadStatus | undefined => state.projects.currentUploads[location];
export const getDownloadStatus = (location: string) =>
  (state: RootState): DownloadStatus | undefined => state.projects.currentDownloads[location];
export const getEntityToRename = (state: RootState) => state.projects.entityToRename;
export const getEntityDownloadLink =
  (file: ProjectEntity) =>
    (state: RootState) => state.projects.downloadLinks[file.id]?.url
export const getEntityPreviewLink =
  (file: ProjectEntity) =>
    (state: RootState) => state.projects.previewLinks[file.id]?.url
export const getEntityPreviewThumbnail =
  (file: ProjectEntity) =>
    (state: RootState) => state.projects.previewLinks[file.id]?.thumbnail
export const getServerStatus = (state: RootState) => state.projects.serverStatus;
export const getStorageToken = (projectId: string) => (state: RootState) => state.projects.sessions[projectId];
export const getSessionToken = (projectId: string) => (state: RootState) => state.projects.sessions[projectId].SessionToken;
export const getPersonalRepoId = (state: RootState) => state.projects.mine?.find(project => project.id.startsWith("user-"))?.id;


type MediaRecord = {
  timestamp: number
  transient?: boolean
}
type UrlMediaRecord = MediaRecord & {
  url: string
}
type BlobMediaRecord = MediaRecord & {
  thumbnail: string | null
}
type MediaMap = Record<string, UrlMediaRecord | BlobMediaRecord>
const MediaCacheTransform = createTransform<MediaMap, MediaMap>(
  (inboundState: MediaMap) => {
    const cacheMap: MediaMap = {};
    const limit = Date.now() - 604800000
    for (const key in inboundState) {
      const value = inboundState[key];
      if (!value.transient && value.timestamp > limit) {
        cacheMap[key] = value;
      }
    }
    return cacheMap;
  },
  (outboundState: MediaMap) => outboundState,
  { whitelist: ['downloadLinks', 'loadedThumbnails'] }
);

const persistConfig = {
  key: 'projects',
  storage: CapacitorStorage,
  transforms: [MediaCacheTransform],
  whitelist: ['downloadLinks', 'loadedThumbnails', 'sessions'],
};
export default (Capacitor.isNativePlatform()
  ? persistReducer<ProjectsState>(persistConfig, projectsSlice.reducer)
  : projectsSlice.reducer) as Reducer<ProjectsState>;
